import { useState } from "react";
import { Upload } from "lucide-react";

/**
 * Drag-and-drop file zone. Falls back to click-to-browse.
 * Props:
 *   onFiles(files: File[]): called when files are dropped or selected
 *   multiple: whether to accept multiple files (default false)
 *   accept: file types accept string, e.g. "image/*"
 *   label: display label
 *   testid: data-testid for the input
 *   disabled: disable interaction
 */
export default function DropZone({
  onFiles,
  multiple = false,
  accept = "image/*",
  label,
  testid,
  disabled = false,
  helperText,
}) {
  const [dragging, setDragging] = useState(false);

  const matchesAccept = (file) => {
    const list = (accept || "").split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (list.length === 0) return true;
    const type = (file.type || "").toLowerCase();
    const name = (file.name || "").toLowerCase();
    return list.some((pat) => {
      if (pat.endsWith("/*")) return type.startsWith(pat.slice(0, -1));
      if (pat.startsWith(".")) return name.endsWith(pat);
      return type === pat;
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer?.files || []).filter(matchesAccept);
    if (files.length) onFiles(multiple ? files : [files[0]]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const onChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) onFiles(multiple ? files : [files[0]]);
    e.target.value = "";
  };

  return (
    <label
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDragEnd={handleDragLeave}
      className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 text-center transition ${
        dragging
          ? "border-amber-700 bg-amber-50"
          : "border-stone-300 bg-white hover:border-amber-600 hover:bg-amber-50/50"
      } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
      data-testid={testid ? `${testid}-dropzone` : undefined}
    >
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={onChange}
        disabled={disabled}
        data-testid={testid}
        className="hidden"
      />
      <div className="rounded-full bg-amber-100 p-2">
        <Upload className="h-5 w-5 text-amber-800" />
      </div>
      <div className="text-sm font-medium text-stone-800">
        {dragging ? "Drop to upload" : label || "Click to upload or drag an image here"}
      </div>
      <div className="text-xs text-stone-500">
        {helperText ||
          (multiple ? "Drop one or more images" : "Drop a single image") + " · PNG / JPG / WebP"}
      </div>
    </label>
  );
}
